import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { JdStatus } from "../types/contract";
import { qk, useJdStatus } from "./queries";

export type { JdStatus };

// JD Ops connection lifecycle: status check, connect (OAuth popup that AUTO-CLOSES on
// success so the callback's JSON page never lingers), and disconnect. Shared by the
// JD-pull modal and the profile menu. The status itself is the shared query, so a
// screen that already loaded it and this hook agree.
export function useJdConnect() {
  const qc = useQueryClient();
  // Do not fetch on mount. Callers that need a status call refresh(); a screen that
  // already enabled useJdStatus shares whatever is in the cache.
  const { data } = useJdStatus(false);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    return qc.fetchQuery({
      queryKey: qk.jdStatus,
      queryFn: () => api.jdStatus(),
      staleTime: 0,
    });
  }, [qc]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    // Open the popup synchronously (user gesture), then navigate it to JD's authorize URL.
    const popup = window.open("", "jd_oauth", "width=620,height=760");
    if (!popup) return;
    setConnecting(true);
    api
      .jdAuthorizeUrl()
      .then((url) => {
        popup.location.href = url;
      })
      .catch(() => popup.close());
    // Poll status; the moment the callback lands, close the popup (dismiss the JSON page) and move on.
    pollRef.current = window.setInterval(async () => {
      if (popup.closed) {
        stopPoll();
        setConnecting(false);
        void refresh();
        return;
      }
      try {
        const s = await api.jdStatus();
        if (s.connected) {
          stopPoll();
          try {
            popup.close();
          } catch {
            /* cross-origin close guard */
          }
          setConnecting(false);
          qc.setQueryData(qk.jdStatus, s);
        }
      } catch {
        /* keep polling */
      }
    }, 1500);
  }, [qc, refresh, stopPoll]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    try {
      await api.jdDisconnect();
      await refresh();
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  useEffect(() => () => stopPoll(), [stopPoll]);

  return {
    status: data ?? null,
    refresh,
    connect,
    disconnect,
    connecting,
    busy,
  };
}
